// Dependencies
import React, {
  Ref,
  useState,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
  KeyboardEventHandler,
  useEffect,
} from 'react';
import { useClickAway } from 'react-use';

//Types
import { DropDownProps, ImperativeRefInput } from './types';
import {
  onChangeValue,
  ImperativeRefList,
  OptionSelected,
} from '../DropDownList/types';

// Styled Components
import {
  Container,
  DropDownHeader,
  DropDownContainer,
  SelectedOption,
  Label,
  ArrowContainer,
  ArrowDown,
} from './styles';

// Components
import DropDownList from '../DropDownList';

// Media
import { ErrorMessage } from '../../InputText/styles';

const DropDownInput = (
  {
    inputSize,
    typeDropDown,
    placeholder,
    labelText,
    options,
    onChangeSelected,
    defaultValue,
    selectedOption: selectedOptionProp,
    error,
    borderError,
    isDisabled,
    handleSelectedOption,
    placement,
    portal,
    optionsChange = false,
    id,
    ...props
  }: DropDownProps,
  innerRef: Ref<ImperativeRefInput>
): JSX.Element => {
  const [isActive, setIsActive] = useState(false);
  const [selectedOption, setSelectedOption] = useState<
    OptionSelected | undefined
  >(() =>
    options?.find((opt) => {
      if (opt.value === defaultValue) {
        return opt.value === defaultValue;
      }
      if (opt.label === defaultValue) {
        return opt.label === defaultValue;
      }
    })
  );

  const listRef = useRef<ImperativeRefList>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  // Sincronizar selectedOption prop con estado local
  useEffect(() => {
    if (selectedOptionProp !== undefined) {
      const option = options?.find((opt) => opt.value === selectedOptionProp);
      if (option) {
        setSelectedOption(option);
      } else if (selectedOptionProp === '' || selectedOptionProp === null) {
        setSelectedOption(undefined);
      }
    }
  }, [selectedOptionProp, options]);

  useClickAway(containerRef, (event) => {
    // En modo portal el panel vive fuera de containerRef (en document.body): un
    // click en una opción sería "click fuera" y cerraría el panel antes de que
    // el onClick de la opción seleccione. Ignora clicks dentro del panel.
    const target = event?.target as Node | undefined;
    if (portal && target && listRef.current?.getListEl?.()?.contains(target)) {
      return;
    }
    listRef.current?.setHidden();
    setIsActive(false);
  });

  const onChangeList = useCallback<onChangeValue>((option) => {
    setSelectedOption(option);
    if (onChangeSelected) {
      onChangeSelected(option);
    }
    if (handleSelectedOption) {
      option && handleSelectedOption(option?.value);
    }
    // Bug fix: deps array vacío capturaba `onChangeSelected` y `handleSelectedOption`
    // del primer render → stale closure. Causaba que cambios en el callback del
    // padre no se aplicaran (e.g. filtros `selectorData` que actualizan entre renders).
  }, [onChangeSelected, handleSelectedOption]);

  const clickedOption = useCallback(() => {
    listRef.current?.setVisibleDropDown();
    setIsActive(true);
  }, []);

  useImperativeHandle(innerRef, () => ({
    clearSelected: () => {
      setSelectedOption(undefined);
      setIsActive(false);
    },
    setSelectedOption: (option) => onChangeList(option),
  }));

  const pressFocus: KeyboardEventHandler = useCallback((event) => {
    if (event.key === ' ') {
      event.preventDefault();
      clickedOption();
    }
  }, []);

  // El texto visible se busca EN VIVO en `options` por `value`, en vez de usar
  // el `label` guardado en el estado.
  //
  // `selectedOption` es una foto del momento en que se eligió la opción (o del
  // primer render). Mientras todas las etiquetas del catálogo estaban escritas
  // en catalán a mano daba igual, porque nunca cambiaban. Desde que se traducen
  // (2026-08-04) sí importa: al cambiar de idioma la lista se recalcula pero la
  // foto no, y el campo se quedaba mostrando el idioma anterior — «Arcén» con
  // la lista en catalán enseñando «Voral». Cazado por el usuario a los cinco
  // minutos de probarlo.
  //
  // Si el valor no está en `options` (listas que se filtran por concesión, por
  // equipo…) se conserva la etiqueta guardada: mismo comportamiento que antes,
  // nunca un campo en blanco.
  const visibleLabel =
    options?.find((opt) => opt.value === selectedOption?.value)?.label ??
    selectedOption?.label;

  const renderSelectedOption = useCallback(
    () => <SelectedOption>{visibleLabel}</SelectedOption>,
    [visibleLabel]
  );
  const renderSelectedOptionEmpty = useCallback(
    () => (
      <SelectedOption variant="regular" colorText="greySoftLight">
        {placeholder}
      </SelectedOption>
    ),
    [placeholder]
  );
  return (
    <Container
      inputSize={inputSize}
      labelText={!!labelText}
      typeDropDown={typeDropDown}
      {...props}
    >
      {labelText && <Label as="label" htmlFor={id} colorText="black">{labelText}</Label>}
      <DropDownContainer
        id={id}
        ref={containerRef}
        onClick={(e) => {
          e.stopPropagation();
          isDisabled ? undefined : clickedOption();
        }}
      >
        <DropDownHeader
          typeDropDown={typeDropDown}
          isActive={isActive}
          borderError={borderError}
          tabIndex={0}
          onKeyDownCapture={pressFocus}
          isDisabled={isDisabled}
        >
          {optionsChange
            ? selectedOption &&
              options.find(
                (option) => option.value === selectedOption.value
              ) !== undefined
              ? renderSelectedOption()
              : renderSelectedOptionEmpty()
            : selectedOption
              ? renderSelectedOption()
              : renderSelectedOptionEmpty()}

          <ArrowContainer
            onClick={(e) => {
              // Keep event local but still trigger the dropdown toggle when clicking the arrow
              e.stopPropagation();
              isDisabled ? undefined : clickedOption();
            }}
          >
            <ArrowDown />
          </ArrowContainer>
        </DropDownHeader>

        {
          <DropDownList
            selectedOption={selectedOption}
            setSelectedAndCallback={onChangeList}
            typeDropDown={typeDropDown}
            options={options}
            placement={placement}
            portal={portal}
            anchorRef={containerRef}
            ref={listRef}
          />
        }
      </DropDownContainer>
      {error && (
        <ErrorMessage colorText={'lightCoralRed'} size={'XXS'}>
          {error}
        </ErrorMessage>
      )}
    </Container>
  );
};

export default forwardRef(DropDownInput);
